import { Injectable, signal } from '@angular/core';
import { Subject } from 'rxjs';

/** The two start-page guides; only one may be on screen at a time. */
export type UserGuideKind = 'tour' | 'for-you';

/**
 * Coordinates opening the start-page user guide from the Help menu without query parameters.
 * Pending is consumed in the tour component after navigation; if the route does not recreate the
 * component (already on start), {@link completePendingOpenAfterNavigate} emits.
 *
 * Also tracks which guide is currently active so the two independent guide components
 * (`start-page-tour`, `for-you-user-guide`) stay mutually exclusive without the start page
 * having to coordinate them.
 */
@Injectable({
  providedIn: 'root',
})
export class UserGuideStartRequestService {
  private pendingFromMenu = false;

  private readonly openFromMenu$ = new Subject<void>();

  /** Observable for same-route navigation (StartPage is not recreated). */
  readonly menuOpenRequested$ = this.openFromMenu$.asObservable();

  private readonly _activeGuide = signal<UserGuideKind | null>(null);

  /** Which guide is currently shown, or null. */
  readonly activeGuide = this._activeGuide.asReadonly();

  setActiveGuide(guide: UserGuideKind): void {
    this._activeGuide.set(guide);
  }

  /** Clears the active guide only if `guide` is the one currently set (avoids clobbering a takeover). */
  clearActiveGuide(guide: UserGuideKind): void {
    if (this._activeGuide() === guide) {
      this._activeGuide.set(null);
    }
  }

  markPendingOpenFromMenu(): void {
    this.pendingFromMenu = true;
  }

  consumePendingOpenFromMenu(): boolean {
    if (!this.pendingFromMenu) {
      return false;
    }
    this.pendingFromMenu = false;
    return true;
  }

  /**
   * Call after `router.navigate` to the start page. If the start component was recreated,
   * {@link consumePendingOpenFromMenu} already cleared the flag. If we stayed on start, open via stream.
   */
  completePendingOpenAfterNavigate(): void {
    if (!this.pendingFromMenu) {
      return;
    }
    this.pendingFromMenu = false;
    this.openFromMenu$.next();
  }
}
